// Shared marketing-site shell — ports the Brand, Nav, Footer atoms from
// design_handoff_rev9_portal/prototype/Rev9 Site.html. Inline styles + CSS
// classes preserved per the handoff.
//
// Differences vs the portal shell (web/src/components/shell.tsx):
//   - Marketing BrandMark uses plain "9" (portal uses italic <em>9</em>)
//   - Marketing Brand uses <a> (portal uses <button>)
//   - Marketing Nav has Home / Approach / Contact + a "Client portal →" CTA
//     that navigates to /sign-in (was setView/onSignIn in the prototype)

/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from 'react';

import { EntityDisclaimer } from './EntityDisclaimer';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export function MarketingBrandMark() {
  return <span className="brand-mark">9</span>;
}

export function MarketingBrand({ onClick }: { onClick?: () => void }) {
  return (
    <a className="brand" onClick={onClick} style={{ cursor: 'pointer' }}>
      <MarketingBrandMark />
      <span className="brand-name">rev<em>9</em>&nbsp;capital</span>
    </a>
  );
}

const NAV_ITEMS = [
  { id: 'home',     label: 'Home',     path: '/' },
  { id: 'approach', label: 'Approach', path: '/approach' },
  { id: 'contact',  label: 'Contact',  path: '/contact' },
] as const;

export function MarketingNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const active = (path: string) => location.pathname === path;
  return (
    <header className="nav">
      <div className="nav-inner">
        <MarketingBrand onClick={() => navigate('/')} />
        <nav className="nav-links">
          {NAV_ITEMS.map(it => (
            <Link key={it.id} to={it.path} className="nav-link" data-active={active(it.path) ? '1' : '0'}>
              {it.label}
            </Link>
          ))}
        </nav>
        <button className="nav-cta" onClick={() => navigate('/sign-in')}>Client portal →</button>
      </div>
    </header>
  );
}

export function MarketingFooter() {
  const navigate = useNavigate();
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <h3 className="footer-cta">
              <em>Discretionary</em> capital, on the record.
            </h3>
            <button className="btn" style={{ marginTop: 32 }} onClick={() => navigate('/contact')}>
              Inquire <span className="btn-arrow">→</span>
            </button>
          </div>
          <div className="footer-col">
            <h5>SITE</h5>
            <ul>
              <li><a onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Home</a></li>
              <li><a onClick={() => navigate('/approach')} style={{ cursor: 'pointer' }}>Approach</a></li>
              <li><a onClick={() => navigate('/contact')} style={{ cursor: 'pointer' }}>Contact</a></li>
              <li><a onClick={() => navigate('/sign-in')} style={{ cursor: 'pointer' }}>Client portal</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h5>LEGAL</h5>
            <ul>
              {/* Form ADV link removed — LC-REV9-001 B1: no ADV exists; no
                  surface may imply adviser registration until counsel
                  confirms an effective registration. */}
              <li><a href="#">Privacy</a></li>
              <li><a href="#">Terms</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h5>OFFICE</h5>
            <ul>
              <li>Miami, Florida</li>
              <li>United States</li>
              <li><a href="mailto:desk@rev9.capital">desk@rev9.capital</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-fine">
          <span>© 2026 REV9 CAPITAL LLC</span>
          <span>MIAMI · FLORIDA</span>
        </div>

        <div className="footer-disclosure">
          {/* LC-REV9-001 B1 (operator-ratified, comply-or-halt): the prior
              "registered investment adviser" sentence was FALSE and is
              removed. Add NOTHING here implying registration until counsel
              confirms; the entity disclaimer lands via BH-RAPP-001. */}
          Digital asset markets are volatile; managed accounts carry risk of loss
          including principal.
          <EntityDisclaimer style={{ textTransform: 'none', letterSpacing: 'normal', fontSize: 10 }} />
        </div>
      </div>
    </footer>
  );
}

export function MarketingLayout({ children }: { children: ReactNode }) {
  return (
    <div className="page">
      <MarketingNav />
      {children}
      <MarketingFooter />
    </div>
  );
}
